import { Input, type InputProps } from "antd";
import { type CountryCode, type E164Number } from "libphonenumber-js/min";
import { type ElementRef, forwardRef, type FunctionComponent, useCallback, useMemo } from "react";
import PhoneInput, { parsePhoneNumber } from "react-phone-number-input";

import { cn } from "@btnet/utilities-core";

import { type Countries } from "types/countries";

import { PhoneNumberDisplay } from "commonComponents/utilities/PhoneNumberDisplay/PhoneNumberDisplay";

import "react-phone-number-input/style.css";

import "./CellPhoneField.less";

const PhoneInputComponent = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    const extractInputRef = useCallback(
        (instRef: ElementRef<typeof Input>) => {
            if (typeof ref === "function" && instRef) {
                ref(instRef.input);
            }
        },
        [ref],
    );

    // eslint-disable-next-line react/forbid-elements
    return <Input ref={extractInputRef} {...props} />;
});

export interface ICellPhoneInputProps {
    id: string;
    addInternationalOption?: boolean;
    value: string;
    isBds?: boolean;
    defaultCountryCode: Countries;
    countries?: Countries[];
    onChange: (field: string, value: string) => void;
    onBlur: (field: string) => void;
    readOnly?: boolean;
    "data-testid"?: string;
}

export const CellPhoneField: FunctionComponent<ICellPhoneInputProps> = ({
    id,
    defaultCountryCode,
    value,
    isBds = false,
    countries,
    addInternationalOption,
    onChange,
    onBlur,
    readOnly = false,
    "data-testid": dataTestId,
}) => {
    const handleCellPhoneChanged = useCallback(
        (value?: E164Number) => {
            onChange(id, value ?? "");
        },
        [id, onChange],
    );

    const handleBlur = useCallback(() => onBlur(id), [onBlur, id]);

    const countryCodes = useMemo(() => countries?.map((x) => x as CountryCode), [countries]);

    const phoneInputMemoized = useMemo(
        () =>
            readOnly ? (
                <PhoneNumberDisplay phoneNumber={value} />
            ) : (
                <PhoneInput
                    id="CellPhoneField-CellPhoneNumber"
                    value={parsePhoneNumber(value)?.number}
                    defaultCountry={defaultCountryCode as CountryCode}
                    countries={countryCodes}
                    onChange={handleCellPhoneChanged}
                    onBlur={handleBlur}
                    className={cn({ "bds-input-container": isBds })}
                    inputComponent={PhoneInputComponent}
                    addInternationalOption={addInternationalOption}
                    data-testid={dataTestId}
                />
            ),
        [
            value,
            defaultCountryCode,
            countryCodes,
            handleCellPhoneChanged,
            handleBlur,
            isBds,
            addInternationalOption,
            readOnly,
            dataTestId,
        ],
    );

    return phoneInputMemoized;
};
